'use client'

import posthog from 'posthog-js'
import { ANALYTICS_CONSENT_HEADER, analyticsConsentHeaderValue } from '@/lib/analytics-consent'
import type { ActionResult } from '@/types/actions'

/**
 * Shared fetch wrapper for API routes.
 * Returns ActionResult<T> — use unwrapResult() to convert to throw-on-error for useMutation.
 */
/**
 * Thrown by `unwrapResult` on a failed `ActionResult`. Extends `Error` so
 * every existing `error.message` / `error instanceof Error` consumer keeps
 * working unchanged; `status`/`code`/`details` carry the extra fields
 * `apiCall` already parsed off the HTTP response, so a caller (e.g.
 * `useStandardMutation`'s onError) can route on a machine-readable failure
 * class instead of matching `error.message` text.
 */
export class ApiError extends Error {
  status?: number
  code?: string
  details?: unknown
  /**
   * A designed refusal (a limit the caller shows in its own words), not a defect: the global
   * mutation handler neither reports nor logs it. Set through `markExpectedRefusal`
   * (`expected-refusal.ts`), never by a route's generic failure path.
   */
  expected?: boolean

  constructor(message: string, opts: { status?: number; code?: string; details?: unknown } = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = opts.status
    this.code = opts.code
    this.details = opts.details
  }
}

/**
 * Unwrap an ActionResult into a plain value, throwing on failure.
 * Designed for useMutation's mutationFn which expects thrown errors:
 *   mutationFn: (vars) => addPrompt(vars).then(unwrapResult)
 *
 * Throws `ApiError`, not a plain `Error` (docs/tasks/billing-past-due/plan.md,
 * task 11/warning 10's SCOPE NOTE, now closed): `result.status`/`result.code`
 * (e.g. `'payment_required'`/`'subscription_required'`) are attached to the
 * thrown error so `useStandardMutation`'s onError can detect an
 * entitlement-denied mutation and route to `?tab=billing` instead of
 * toasting the caller's static `errorMessage` (REL-26).
 */
export function unwrapResult<T>(result: ActionResult<T>): T {
  if (!result.success) {
    throw new ApiError(result.error, {
      status: result.status,
      code: result.code,
      details: result.details,
    })
  }
  return result.data as T
}

/**
 * R6.17 AC-6: the banner choice lives only in this browser, so a same-origin call (a relative URL: no
 * CORS preflight, and the value never leaves our host) carries it as a header. The server ties a
 * Suggestion event to the account only for exactly `accepted`. A caller's own value wins.
 */
function addConsentHeader(url: string, headers: Headers): void {
  const sameOrigin = url.startsWith('/') && !url.startsWith('//')
  if (sameOrigin && !headers.has(ANALYTICS_CONSENT_HEADER)) {
    headers.set(ANALYTICS_CONSENT_HEADER, analyticsConsentHeaderValue())
  }
}

export async function apiCall<T = void>(
  url: string,
  options: RequestInit & { timeout?: number } = {}
): Promise<ActionResult<T>> {
  const { timeout = 120_000, ...fetchOptions } = options
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)
  const startedAt = Date.now()

  try {
    // When the body is FormData, let the browser set the multipart boundary in
    // Content-Type automatically. Setting it manually (to 'application/json')
    // strips the boundary parameter and causes the server's request.formData()
    // to throw a 400. Only apply the JSON default for non-FormData bodies.
    const isFormData = fetchOptions.body instanceof FormData
    const headers = new Headers(fetchOptions.headers as HeadersInit | undefined)
    if (!isFormData && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }
    addConsentHeader(url, headers)
    const res = await fetch(url, {
      ...fetchOptions,
      headers,
      signal: controller.signal,
    })
    if (!res.ok) {
      try {
        const json = await res.json()
        // `code` (e.g. 'payment_required') lets the UI route on a machine-readable
        // failure class instead of parsing `error` strings — thread it through
        // whenever the route included one.
        const code = typeof json.code === 'string' ? json.code : undefined
        if (json.error) {
          // Thread through any additional JSON keys beyond `error`/`code` as
          // `details` — e.g. the downgrade gate's `{ error, blockers }` 402 body.
          const extraKeys = Object.keys(json as Record<string, unknown>).filter(
            (key) => key !== 'error' && key !== 'code'
          )
          const details: Record<string, unknown> | undefined =
            extraKeys.length > 0
              ? Object.fromEntries(
                  extraKeys.map((key) => [key, (json as Record<string, unknown>)[key]])
                )
              : undefined
          return {
            success: false,
            error: json.error,
            status: res.status,
            code,
            ...(details ? { details } : {}),
          }
        }
        // Zod-style `{ fieldErrors: { <field>: <message> } }` — flatten to a
        // `field:<name>: <message>` string so callers can parse per-field errors.
        if (json.fieldErrors && typeof json.fieldErrors === 'object') {
          const entry = Object.entries(json.fieldErrors as Record<string, unknown>)[0]
          if (entry) {
            const [field, message] = entry
            return {
              success: false,
              error: `field:${field}: ${String(message)}`,
              status: res.status,
              // Thread `code` here too — consistent with the other two failure
              // branches below, even though a field-validation (422) response
              // is not expected to carry one today.
              code,
            }
          }
        }
        return { success: false, error: 'Request failed', status: res.status, code }
      } catch {
        return { success: false, error: `Server error (${res.status})`, status: res.status }
      }
    }
    // Handle 204 No Content and empty responses
    if (res.status === 204 || res.headers.get('content-length') === '0') {
      return { success: true, data: undefined as T }
    }
    const json = await res.json()
    return { success: true, data: json as T }
  } catch (err) {
    const durationMs = Date.now() - startedAt
    if (err instanceof DOMException && err.name === 'AbortError') {
      if (posthog.__loaded) {
        posthog.capture('api_call_timeout', { url, timeout_ms: timeout, duration_ms: durationMs })
      }
      return { success: false, error: 'Request timed out. Please try again.' }
    }
    if (posthog.__loaded) {
      posthog.capture('api_call_network_error', {
        url,
        duration_ms: durationMs,
        error_message: err instanceof Error ? err.message : 'Unknown error',
      })
    }
    return { success: false, error: 'Network error. Please try again.' }
  } finally {
    clearTimeout(timeoutId)
  }
}
